'use client'

import { motion } from 'framer-motion'
import Link from 'next/link'
import { ShieldCheck, Lock, Eye, Trash2, Mail, FileText, Cookie, AlertCircle } from 'lucide-react'
import { EASE_OUT, EASE_CUSTOM } from '@/lib/animations'

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.9, ease: EASE_CUSTOM },
}

const stagger = (i: number) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.8, delay: i * 0.08, ease: EASE_CUSTOM },
})

const sections = [
  {
    icon: FileText,
    title: '1. Responsable del tratamiento',
    color: 'text-blue-600',
    bg: 'from-blue-50 to-indigo-50',
    content: (
      <>
        <p className="text-slate-600 leading-relaxed mb-3">
          El responsable del tratamiento de los datos personales recogidos a través de este sitio web es:
        </p>
        <ul className="space-y-1.5 text-slate-600 text-sm">
          <li><span className="font-semibold text-slate-800">Responsable:</span> Teodor Chavdarov</li>
          <li><span className="font-semibold text-slate-800">NIF:</span> X8562855R</li>
          <li><span className="font-semibold text-slate-800">Forma jurídica:</span> Trabajador autónomo (empresario individual)</li>
          <li><span className="font-semibold text-slate-800">Nombre comercial:</span> Innovathera Technology</li>
          <li><span className="font-semibold text-slate-800">Domicilio profesional:</span> Calle Costa Verde Nº 5, 3B · 28400 Collado Villalba · Madrid · España</li>
          <li><span className="font-semibold text-slate-800">Correo electrónico (general):</span>{' '}
            <a href="mailto:info@innovatheratech.com" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">
              info@innovatheratech.com
            </a>
          </li>
          <li><span className="font-semibold text-slate-800">Correo electrónico (soporte):</span>{' '}
            <a href="mailto:soporte@innovatheratech.com" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">
              soporte@innovatheratech.com
            </a>
          </li>
          <li><span className="font-semibold text-slate-800">Sitio web:</span>{' '}
            <a href="https://innovatheratech.com" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">
              https://innovatheratech.com
            </a>
          </li>
        </ul>
      </>
    ),
  },
  {
    icon: Eye,
    title: '2. Datos que recogemos',
    color: 'text-indigo-600',
    bg: 'from-indigo-50 to-violet-50',
    content: (
      <>
        <p className="text-slate-600 leading-relaxed mb-3">
          A través del formulario de contacto de este sitio web, podemos recoger los siguientes datos personales:
        </p>
        <ul className="space-y-2 text-slate-600 text-sm list-none">
          {[
            'Nombre y apellidos',
            'Dirección de correo electrónico',
            'Sitio web o URL del negocio (opcional)',
            'Sector o industria en la que opera su empresa',
            'Descripción del problema, necesidad o proyecto a desarrollar',
            'Número de teléfono (si se facilita voluntariamente)',
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-indigo-400" />
              {item}
            </li>
          ))}
        </ul>
        <p className="text-slate-500 text-sm mt-3 italic">
          No recogemos datos sensibles (salud, ideología, religión, etc.) ni datos de menores de 14 años.
        </p>
      </>
    ),
  },
  {
    icon: FileText,
    title: '3. Finalidad del tratamiento',
    color: 'text-violet-600',
    bg: 'from-violet-50 to-purple-50',
    content: (
      <>
        <p className="text-slate-600 leading-relaxed mb-3">
          Los datos facilitados se utilizan exclusivamente para las siguientes finalidades:
        </p>
        <ul className="space-y-2 text-slate-600 text-sm">
          {[
            'Atender y gestionar las consultas o solicitudes recibidas a través del formulario de contacto.',
            'Elaborar y enviar una propuesta comercial personalizada adaptada a las necesidades descritas.',
            'Mantener comunicación comercial relacionada con los servicios de Innovathera Technology',
            'Gestionar la relación precontractual y contractual derivada de la prestación de servicios.',
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-violet-400" />
              {item}
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    icon: ShieldCheck,
    title: '4. Base jurídica del tratamiento',
    color: 'text-emerald-600',
    bg: 'from-emerald-50 to-teal-50',
    content: (
      <p className="text-slate-600 leading-relaxed">
        La base jurídica que legitima el tratamiento de sus datos es el <span className="font-semibold text-slate-800">consentimiento expreso del interesado</span>, otorgado en el momento de cumplimentar y enviar el formulario de contacto, de conformidad con el artículo 6.1.a) del Reglamento General de Protección de Datos (RGPD) y la Ley Orgánica 3/2018, de 5 de diciembre, de Protección de Datos Personales y garantía de los derechos digitales (LOPDGDD). Usted puede retirar su consentimiento en cualquier momento, sin que ello afecte a la licitud del tratamiento previo a su retirada.
      </p>
    ),
  },
  {
    icon: Lock,
    title: '5. Conservación de los datos',
    color: 'text-amber-600',
    bg: 'from-amber-50 to-orange-50',
    content: (
      <p className="text-slate-600 leading-relaxed">
        Los datos personales se conservarán durante el tiempo necesario para atender su solicitud y mantener la relación comercial. En caso de que no se produzca ninguna actividad o comunicación, los datos serán suprimidos transcurridos un máximo de <span className="font-semibold text-slate-800">dos (2) años desde el último contacto</span>. En todo caso, se conservarán durante el plazo legalmente exigido para el cumplimiento de obligaciones legales, contables o fiscales que resulten de aplicación.
      </p>
    ),
  },
  {
    icon: AlertCircle,
    title: '6. Cesión de datos a terceros',
    color: 'text-rose-600',
    bg: 'from-rose-50 to-pink-50',
    content: (
      <p className="text-slate-600 leading-relaxed">
        Innovathera Technology <span className="font-semibold text-slate-800">no cede ni comunica sus datos personales a terceros</span>, salvo en los casos en que exista una obligación legal que lo exija o cuando resulte estrictamente necesario para la prestación de los servicios contratados (por ejemplo, proveedores de alojamiento web o herramientas de gestión de correo bajo acuerdos de encargado de tratamiento conformes al RGPD). En ningún caso se venderán datos a terceros con fines comerciales.
      </p>
    ),
  },
  {
    icon: Eye,
    title: '7. Derechos del interesado (ARCO+)',
    color: 'text-cyan-600',
    bg: 'from-cyan-50 to-sky-50',
    content: (
      <>
        <p className="text-slate-600 leading-relaxed mb-3">
          De conformidad con la normativa vigente en materia de protección de datos, usted tiene derecho a:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { right: 'Acceso', desc: 'Conocer qué datos suyos tratamos y con qué finalidad.' },
            { right: 'Rectificación', desc: 'Solicitar la corrección de datos inexactos o incompletos.' },
            { right: 'Cancelación / Supresión', desc: 'Pedir la eliminación de sus datos cuando ya no sean necesarios.' },
            { right: 'Oposición', desc: 'Oponerse al tratamiento de sus datos en determinadas circunstancias.' },
            { right: 'Limitación', desc: 'Solicitar que se restrinja el tratamiento de sus datos.' },
            { right: 'Portabilidad', desc: 'Recibir sus datos en un formato estructurado y de uso común.' },
          ].map((item) => (
            <div key={item.right} className="bg-white/60 rounded-xl p-3 border border-slate-200/30">
              <p className="font-semibold text-slate-800 text-sm mb-0.5">{item.right}</p>
              <p className="text-slate-500 text-xs leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-slate-600 text-sm mt-4">
          Para ejercer cualquiera de estos derechos, puede ponerse en contacto con nosotros enviando un correo a{' '}
          <a href="mailto:info@innovatheratech.com" className="text-blue-600 hover:text-blue-700 underline underline-offset-2 font-medium">
            info@innovatheratech.com
          </a>{' '}
          indicando el derecho que desea ejercer y adjuntando una copia de su documento de identidad. Asimismo, tiene derecho a presentar una reclamación ante la <span className="font-semibold text-slate-800">Agencia Española de Protección de Datos (AEPD)</span> en <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">www.aepd.es</a>.
        </p>
      </>
    ),
  },
  {
    icon: Cookie,
    title: '8. Política de cookies',
    color: 'text-orange-600',
    bg: 'from-orange-50 to-amber-50',
    content: (
      <>
        <p className="text-slate-600 leading-relaxed mb-3">
          Este sitio web utiliza cookies para mejorar la experiencia del usuario y analizar el tráfico. Concretamente:
        </p>
        <div className="space-y-2 text-sm">
          <div className="bg-white/60 rounded-xl p-3 border border-slate-200/30">
            <p className="font-semibold text-slate-800 mb-1">Cookies analíticas — Google Analytics</p>
            <p className="text-slate-500 leading-relaxed">
              Utilizamos Google Analytics (Universal Analytics / GA4) para recopilar información estadística sobre el uso del sitio web de forma anónima y agregada. Esta herramienta emplea cookies propias de Google (_ga, _gid, _gat) para identificar sesiones únicas. Los datos recogidos no permiten identificar a personas físicas de manera directa. Puede optar por no ser rastreado instalando el complemento de inhabilitación de Google Analytics disponible en <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">tools.google.com/dlpage/gaoptout</a>.
            </p>
          </div>
          <div className="bg-white/60 rounded-xl p-3 border border-slate-200/30">
            <p className="font-semibold text-slate-800 mb-1">Cookies técnicas o necesarias</p>
            <p className="text-slate-500 leading-relaxed">
              Cookies estrictamente necesarias para el correcto funcionamiento del sitio. No requieren consentimiento previo.
            </p>
          </div>
        </div>
        <p className="text-slate-500 text-sm mt-3">
          Puede gestionar o deshabilitar las cookies desde la configuración de su navegador. Tenga en cuenta que la deshabilitación de ciertas cookies puede afectar a la funcionalidad del sitio.
        </p>
      </>
    ),
  },
  {
    icon: ShieldCheck,
    title: '9. Medidas de seguridad',
    color: 'text-teal-600',
    bg: 'from-teal-50 to-emerald-50',
    content: (
      <p className="text-slate-600 leading-relaxed">
        Innovathera Technology ha adoptado las medidas técnicas y organizativas necesarias para garantizar la seguridad de los datos personales y evitar su alteración, pérdida, tratamiento o acceso no autorizado, habida cuenta del estado de la tecnología, la naturaleza de los datos almacenados y los riesgos a que están expuestos. Entre dichas medidas se incluyen el uso de conexiones cifradas (HTTPS/TLS), accesos restringidos por credenciales y políticas de mínimo privilegio.
      </p>
    ),
  },
]

export default function PrivacidadClient() {
  return (
    <section className="pt-28 pb-20 relative z-10">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <motion.div {...fadeUp} className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-5">
            <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-xs font-mono tracking-[0.25em] text-blue-600 uppercase">RGPD Conforme</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
            Política de{' '}
            <span className="text-blue-600">Privacidad</span>
          </h1>
          <p className="text-slate-500 text-lg font-light max-w-2xl mx-auto leading-relaxed">
            En Innovathera Technology nos tomamos muy en serio la protección de sus datos personales. Esta política describe cómo recogemos, utilizamos y protegemos su información.
          </p>
          <p className="text-xs font-mono text-slate-400 mt-4 tracking-wider uppercase">
            Última actualización: Mayo 2026
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-5">
          {sections.map((s, i) => {
            const Icon = s.icon
            return (
              <motion.div
                key={s.title}
                {...stagger(i)}
                className="glass-card rounded-2xl p-6 md:p-8"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className={`shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${s.bg} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${s.color}`} />
                  </div>
                  <h2 className="text-lg font-bold text-slate-900 leading-snug pt-1.5">{s.title}</h2>
                </div>
                <div className="pl-14">
                  {s.content}
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-10 glass-card rounded-2xl p-8 text-center"
        >
          <Trash2 className="w-8 h-8 text-slate-400 mx-auto mb-3" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">¿Quiere ejercer sus derechos o tiene alguna duda?</h3>
          <p className="text-slate-500 text-sm mb-5 max-w-lg mx-auto">
            Puede contactarnos en cualquier momento para solicitar acceso, rectificación, cancelación u oposición al tratamiento de sus datos personales.
          </p>
          <a
            href="mailto:info@innovatheratech.com"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-all shadow-[0_4px_16px_rgba(37,99,235,0.3)] active:scale-95"
          >
            <Mail className="w-4 h-4" />
            info@innovatheratech.com
          </a>
        </motion.div>

      </div>
    </section>
  )
}
